import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@brightside/core/auth';
import { environment } from '@brighthub/environment';
import { firstValueFrom } from 'rxjs';
import type {
  AgentConnectorDefinition,
  AgentConnectorMcpServerRef,
  AgentConnectorTool,
  ConnectorCompleteResponse,
  ConnectorDisconnectResponse,
  ConnectorInitiateResponse,
  ConnectorMcpServer,
  ConnectorStatus,
  ConnectorTransportType,
} from './connector.models';

/**
 * Reads agent definitions from command-center and manages user connector OAuth
 * through the Copilot API.
 */
@Injectable({ providedIn: 'root' })
export class ConnectorApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly commandCenterBaseUrl = requireBaseUrl('command-center');
  private readonly copilotBaseUrl = requireBaseUrl('copilot');

  /**
   * Loads the connector-related definition for an agent identifier.
   *
   * @param identifier - The full command-center agent identifier.
   * @returns The agent's tool and MCP server references.
   */
  async getAgentDefinition(identifier: string): Promise<AgentConnectorDefinition> {
    return this.get(
      `${this.commandCenterBaseUrl}/agent/${encodeURIComponent(identifier)}`,
      parseAgentDefinition
    );
  }

  /**
   * Loads and validates one MCP server definition.
   *
   * @param mcpServerId - The opaque MCP server identifier.
   * @returns The normalized MCP server definition.
   */
  async getMcpServer(mcpServerId: string): Promise<ConnectorMcpServer> {
    return this.get(
      `${this.commandCenterBaseUrl}/mcp-servers/${encodeURIComponent(mcpServerId)}`,
      parseMcpServer
    );
  }

  /**
   * Reads the current user's connection status for the requested MCP servers.
   *
   * @param mcpServerIds - Opaque MCP server identifiers required by the active agent.
   * @returns One status entry per connector reported by the API.
   */
  async getConnectorStatuses(mcpServerIds: string[]): Promise<ConnectorStatus[]> {
    if (mcpServerIds.length === 0) return [];
    return this.post(
      `${this.copilotBaseUrl}/connectors/status`,
      { mcp_server_ids: mcpServerIds },
      parseConnectorStatuses
    );
  }

  /**
   * Starts OAuth for one MCP connector.
   *
   * @param mcpServerId - The opaque MCP server identifier.
   * @returns An authorization URL from the API or an already-connected result.
   */
  async initiateConnector(mcpServerId: string): Promise<ConnectorInitiateResponse> {
    return this.post(
      `${this.copilotBaseUrl}/connectors/${encodeURIComponent(mcpServerId)}/initiate`,
      undefined,
      parseConnectorInitiateResponse
    );
  }

  /**
   * Exchanges a returned OAuth session for a connector credential.
   *
   * @param sessionId - The OAuth session identifier from the callback URL.
   * @param mcpServerId - The opaque MCP server identifier stored before redirect.
   * @returns Whether the connector is now connected.
   */
  async completeConnector(
    sessionId: string,
    mcpServerId: string
  ): Promise<ConnectorCompleteResponse> {
    return this.post(
      `${this.copilotBaseUrl}/connectors/complete`,
      { session_id: sessionId, mcp_server_id: mcpServerId },
      parseConnectorCompleteResponse
    );
  }

  /**
   * Removes the current user's credential for one MCP connector.
   *
   * @param mcpServerId - The opaque MCP server identifier.
   * @returns Whether the connector was disconnected.
   */
  async disconnectConnector(
    mcpServerId: string
  ): Promise<ConnectorDisconnectResponse> {
    return this.post(
      `${this.copilotBaseUrl}/connectors/${encodeURIComponent(mcpServerId)}/disconnect`,
      undefined,
      parseConnectorDisconnectResponse
    );
  }

  private async get<T>(url: string, parse: (value: unknown) => T): Promise<T> {
    const headers = await this.authorizationHeaders();
    return parse(await firstValueFrom(this.http.get<unknown>(url, { headers })));
  }

  private async post<T>(
    url: string,
    body: unknown,
    parse: (value: unknown) => T
  ): Promise<T> {
    const headers = (await this.authorizationHeaders())
      .set('Content-Type', 'application/json')
      .set('X-Command-Center-Env', environment.commandCenterEnvironment);
    return parse(
      await firstValueFrom(this.http.post<unknown>(url, body ?? {}, { headers }))
    );
  }

  private async authorizationHeaders(): Promise<HttpHeaders> {
    const token = await this.auth.getAccessToken();
    if (!token) throw new Error('No authentication token available');
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }
}

function requireBaseUrl(endpoint: string): string {
  const baseUrl = environment.apiEndpoints[endpoint]?.trim();
  if (!baseUrl) {
    throw new Error(`environment.apiEndpoints['${endpoint}'] is not configured.`);
  }
  return baseUrl.replace(/\/$/, '');
}

function parseAgentDefinition(value: unknown): AgentConnectorDefinition {
  const record = objectValue(value, 'agent definition');
  return {
    tools: arrayValue(record['tools'], 'agent tools').map(parseAgentTool),
    mcp_servers: arrayValue(
      record['mcp_servers'],
      'agent MCP servers'
    ).map(parseAgentMcpServerRef),
  };
}

function parseAgentTool(value: unknown): AgentConnectorTool {
  const record = objectValue(value, 'agent tool');
  const toolConfig = record['tool_config'];
  return {
    ...(stringValue(record['tool_name'])
      ? { tool_name: stringValue(record['tool_name']) }
      : {}),
    ...(stringValue(record['name']) ? { name: stringValue(record['name']) } : {}),
    ...(isRecord(toolConfig) ? { tool_config: toolConfig } : {}),
  };
}

function parseAgentMcpServerRef(value: unknown): AgentConnectorMcpServerRef {
  const record = objectValue(value, 'agent MCP server');
  const transportType = connectorTransportType(record['transport_type']);
  return {
    ...(stringValue(record['server_id'])
      ? { server_id: stringValue(record['server_id']) }
      : {}),
    ...(stringValue(record['name']) ? { name: stringValue(record['name']) } : {}),
    ...(stringValue(record['display_name'])
      ? { display_name: stringValue(record['display_name']) }
      : {}),
    ...(stringValue(record['server_name'])
      ? { server_name: stringValue(record['server_name']) }
      : {}),
    ...(transportType ? { transport_type: transportType } : {}),
    ...(record['connector_provider'] === null || stringValue(record['connector_provider'])
      ? { connector_provider: stringValue(record['connector_provider']) ?? null }
      : {}),
  };
}

function parseMcpServer(value: unknown): ConnectorMcpServer {
  const record = objectValue(value, 'MCP server');
  const serverId = requiredString(record['server_id'], 'MCP server id');
  const name = requiredString(record['name'], 'MCP server name');
  const displayName = requiredString(
    record['display_name'],
    'MCP server display name'
  );
  const transportType = connectorTransportType(record['transport_type']);
  if (!transportType) throw new Error('Invalid MCP server transport type.');
  return {
    server_id: serverId,
    name,
    display_name: displayName,
    transport_type: transportType,
    connector_provider: stringValue(record['connector_provider']) ?? null,
  };
}

function parseConnectorStatuses(value: unknown): ConnectorStatus[] {
  const record = objectValue(value, 'connector status response');
  return arrayValue(record['statuses'], 'connector statuses').map((item) => {
    const status = objectValue(item, 'connector status');
    if (typeof status['connected'] !== 'boolean') {
      throw new Error('Invalid connector connected status.');
    }
    return {
      mcp_server_id: requiredString(status['mcp_server_id'], 'MCP server id'),
      provider: trimmedString(status['provider'], 'connector provider'),
      connected: status['connected'],
      error: stringValue(status['error']) ?? null,
    };
  });
}

function parseConnectorInitiateResponse(value: unknown): ConnectorInitiateResponse {
  const record = objectValue(value, 'connector initiate response');
  if (typeof record['already_connected'] !== 'boolean') {
    throw new Error('Invalid connector initiate response.');
  }
  const authorizationUrl = record['authorization_url'];
  if (authorizationUrl !== null && typeof authorizationUrl !== 'string') {
    throw new Error('Invalid connector authorization URL.');
  }
  return {
    authorization_url: authorizationUrl,
    already_connected: record['already_connected'],
  };
}

function parseConnectorCompleteResponse(value: unknown): ConnectorCompleteResponse {
  const record = objectValue(value, 'connector complete response');
  if (typeof record['connected'] !== 'boolean') {
    throw new Error('Invalid connector complete response.');
  }
  return {
    mcp_server_id: requiredString(record['mcp_server_id'], 'MCP server id'),
    connected: record['connected'],
  };
}

function parseConnectorDisconnectResponse(value: unknown): ConnectorDisconnectResponse {
  const record = objectValue(value, 'connector disconnect response');
  if (typeof record['disconnected'] !== 'boolean') {
    throw new Error('Invalid connector disconnect response.');
  }
  return {
    mcp_server_id: requiredString(record['mcp_server_id'], 'MCP server id'),
    disconnected: record['disconnected'],
  };
}

function connectorTransportType(value: unknown): ConnectorTransportType | undefined {
  return value === 'http_bearer' ||
    value === 'agentcore' ||
    value === 'connector_oauth'
    ? value
    : undefined;
}

function objectValue(value: unknown, name: string): Record<string, unknown> {
  if (!isRecord(value)) throw new Error(`Invalid ${name}.`);
  return value;
}

function arrayValue(value: unknown, name: string): unknown[] {
  if (!Array.isArray(value)) throw new Error(`Invalid ${name}.`);
  return value;
}

function requiredString(value: unknown, name: string): string {
  const parsed = stringValue(value);
  if (!parsed) throw new Error(`Invalid ${name}.`);
  return parsed;
}

function trimmedString(value: unknown, name: string): string {
  if (typeof value !== 'string') throw new Error(`Invalid ${name}.`);
  return value.trim();
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
